import React from 'react';

import { AxisRangeValues, AxisScale, isAxisRangeValid } from '@neptune/shared/widgets-domain';

type ValidationResult = {
  valid: boolean;
  errorMessage?: string;
};

export type AxisRangeValuesProps = {
  minValue?: number;
  maxValue?: number;
  onChange?: (range: { min: number | undefined; max: number | undefined }) => void;
  validate: (range: [number | undefined, number | undefined]) => ValidationResult;
};

export function useAxisRangeValues({
  minValue,
  maxValue,
  onChange,
  validate,
}: AxisRangeValuesProps): AxisRangeValues {
  const [error, setError] = React.useState<{
    error: boolean;
    errorMessage?: string;
  }>();

  React.useEffect(() => {
    const validateResult = validate([minValue, maxValue]);

    setError({
      error: !validateResult.valid,
      errorMessage: !validateResult.valid ? validateResult.errorMessage : undefined,
    });
  }, [minValue, maxValue, validate]);

  const resetValues = React.useCallback(() => {
    onChange?.({
      min: undefined,
      max: undefined,
    });
  }, [onChange]);

  const handleMinChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = e.target.value ? Number(e.target.value) : undefined;
      onChange?.({
        max: maxValue,
        min: newMin,
      });
    },
    [onChange, maxValue],
  );

  const handleMaxChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = e.target.value ? Number(e.target.value) : undefined;
      onChange?.({
        max: newMax,
        min: minValue,
      });
    },
    [minValue, onChange],
  );

  return {
    minValue,
    maxValue,
    handleMinChange,
    handleMaxChange,
    resetValues,
    error: error?.error,
    errorMessage: error?.errorMessage,
  };
}

export function useAxisRangeValuesWithValidation({
  minValue,
  maxValue,
  onChange,
  axisScale,
}: Pick<AxisRangeValuesProps, 'minValue' | 'maxValue' | 'onChange'> & {
  axisScale?: AxisScale;
}) {
  const validate = React.useCallback(
    (range: [number | undefined, number | undefined]) => isAxisRangeValid(range, axisScale),
    [axisScale],
  );

  return useAxisRangeValues({
    minValue,
    maxValue,
    onChange,
    validate,
  });
}
